import { FC, Fragment, useRef } from 'react';
import { SimpleExpression, SimpleBinaryExpression, ExpressionItem, LogicalOperator, FieldMetadata, OperatorInfo } from '../../models/expression';
import { SimpleExpressionForm } from './SimpleExpressionForm';
import { LogicalOperatorToggle } from './LogicalOperatorToggle';
import { Button } from 'primereact/button';
import { SelectButton } from 'primereact/selectbutton';
import styles from './RuleGroup.module.css';

interface RuleGroupProps {
  group: SimpleExpression;
  level: number;
  onUpdate: (group: SimpleExpression) => void;
  fieldMetadata?: FieldMetadata[];
  rightFieldMetadata?: FieldMetadata[];
  operators?: OperatorInfo[];
  allowCustomFields?: boolean;
}

export const RuleGroup: FC<RuleGroupProps> = ({ group, level, onUpdate, fieldMetadata, rightFieldMetadata, operators, allowCustomFields }) => {
  const expressions = group.expressions || [];
  const hasMultipleExpressions = expressions.length > 1;

  // Rows are edited in place, so they need identities that survive a delete: keying by array
  // index hands a deleted row's react-hook-form state to the row that shifts up into its slot.
  const rowIds = useRef<number[]>([]);
  const nextRowId = useRef(0);
  if (rowIds.current.length !== expressions.length) {
    rowIds.current = expressions.map((_, index) => rowIds.current[index] ?? nextRowId.current++);
  }

  const hasIncompleteExpression =
    expressions.length === 0 ||
    expressions.some(
      (item) =>
        !item.expressionGroup &&
        (!item.expression || !item.expression.left || !item.expression.operator || !item.expression.right)
    );

  const handleLogicalOperatorChange = (value: LogicalOperator) => {
    onUpdate({
      ...group,
      logicalOperator: value,
    });
  };

  const handleIsNotChange = (value: boolean) => {
    onUpdate({
      ...group,
      isNot: value,
    });
  };

  const handleUpdateExpression = (index: number, expression: SimpleBinaryExpression | null) => {

    if (!expression) {  
      return;
    }
    
    const updatedExpressions = [...expressions];
    
    // Check if the expression actually changed to avoid unnecessary updates
    const existingExpression = updatedExpressions[index]?.expression;
    if (
      existingExpression &&
      existingExpression.left === expression.left &&
      existingExpression.operator === expression.operator &&
      JSON.stringify(existingExpression.right) === JSON.stringify(expression.right) &&
      (existingExpression.isNot ?? false) === (expression.isNot ?? false)
    ) {  
      return; // No change, don't update
    }
    
    if (index >= updatedExpressions.length) {  
      updatedExpressions.push({
        expression,
        expressionGroup: null,
      });
    } else {  
      updatedExpressions[index] = {
        ...updatedExpressions[index],
        expression,
      };
    }

    const updatedGroup = {
      ...group,
      expressions: updatedExpressions,
    };
    onUpdate(updatedGroup);
  };

  const handleDeleteExpression = (index: number) => {
    const updatedExpressions = expressions.filter((_, i) => i !== index);
    rowIds.current = rowIds.current.filter((_, i) => i !== index);
    onUpdate({
      ...group,
      // Group-level NOT only has a UI control while the group has multiple conditions,
      // so clear it when the group shrinks below two to avoid an unreachable flag.
      isNot: updatedExpressions.length > 1 ? group.isNot : false,
      expressions: updatedExpressions.length > 0 ? updatedExpressions : null,
    });
  };

  const handleUpdateNestedGroup = (index: number, updatedGroup: SimpleExpression) => {
    const nestedExpressions = updatedGroup.expressions || [];

    if (nestedExpressions.length === 0) {
      handleDeleteExpression(index);
      return;
    }

    const updatedExpressions = [...expressions];

    if (nestedExpressions.length === 1) {
      updatedExpressions[index] = nestedExpressions[0];
    } else {
      updatedExpressions[index] = {
        ...updatedExpressions[index],
        expressionGroup: updatedGroup,
      };
    }

    onUpdate({
      ...group,
      expressions: updatedExpressions,
    });
  };

  const handleCreateNestedGroup = (index: number) => {
    const currentItem = expressions[index];
    if (!currentItem.expression) return;

    const nestedGroup: ExpressionItem = {
      expression: null,
      expressionGroup: {
        expressions: [
          {
            expression: currentItem.expression,
            expressionGroup: null,
          },
          {
            expression: null,
            expressionGroup: null,
          },
        ],
        logicalOperator: group.logicalOperator === LogicalOperator.And ? LogicalOperator.Or : LogicalOperator.And,
        isNot: false,
      },
    };

    const updatedExpressions = [...expressions];
    updatedExpressions[index] = nestedGroup;

    onUpdate({
      ...group,
      expressions: updatedExpressions,
    });
  };

  const handleAddRule = () => {
    const newItem: ExpressionItem = {
      expression: null,
      expressionGroup: null,
    };

    onUpdate({
      ...group,
      expressions: [...expressions, newItem],
    });
  };

  const getConnectionCellClass = (index: number, total: number) => {
    if (index === 0) return `${styles['connection-cell']} ${styles['connection-cell-first']}`;
    if (index === total - 1) return `${styles['connection-cell']} ${styles['connection-cell-last']}`;
    return `${styles['connection-cell']} ${styles['connection-cell-middle']}`;
  };

  const getExpressionCellClass = (index: number, total: number) => {
    if (index === 0) return `${styles['expression-cell']} ${styles['expression-cell-first']}`;
    if (index === total - 1) return `${styles['expression-cell']} ${styles['expression-cell-last']}`;
    return `${styles['expression-cell']} ${styles['expression-cell-middle']}`;
  };

  return (
    <div className="space-y-3">

      {!hasMultipleExpressions && (
        <div className={styles['single-column']}>
          {expressions.length === 0 ? (
            <SimpleExpressionForm
              initialValue={undefined}
              onCancel={() => {}}
              onChange={(expr) => handleUpdateExpression(0, expr)}
              showDelete={false}
              onAddGroup={() => {}}
              showAddGroup={false}
              fieldMetadata={fieldMetadata}
              rightFieldMetadata={rightFieldMetadata}
              operators={operators}
              allowCustomFields={allowCustomFields}
            />
          ) : (
            expressions.map((item, index) =>
              item.expressionGroup ? (
                <div key={rowIds.current[index]} className={styles['expression-group']}>
                  <RuleGroup
                    group={item.expressionGroup}
                    level={level + 1}
                    onUpdate={(updatedGroup) => handleUpdateNestedGroup(index, updatedGroup)}
                    fieldMetadata={fieldMetadata}
                    rightFieldMetadata={rightFieldMetadata}
                    operators={operators}
                    allowCustomFields={allowCustomFields}
                  />
                </div>
              ) : (
                <SimpleExpressionForm
                  key={rowIds.current[index]}
                  initialValue={item.expression || undefined}
                  onCancel={() => handleDeleteExpression(index)}
                  onChange={(expr) => handleUpdateExpression(index, expr)}
                  showDelete
                  onAddGroup={() => handleCreateNestedGroup(index)}
                  showAddGroup={hasMultipleExpressions && !item.expressionGroup}
                  fieldMetadata={fieldMetadata}
                  rightFieldMetadata={rightFieldMetadata}
                  operators={operators}
                  allowCustomFields={allowCustomFields}
                />
              )
            )
          )}
        </div>
      )}

      {hasMultipleExpressions && (
        <div className={styles.grid}>
          {expressions.map((item, index) => (
            <Fragment key={rowIds.current[index]}>
              <div className={getConnectionCellClass(index, expressions.length)}>
                {index === 0 && (
                  <div className={styles['logical-operator']}>
                    <div className={styles['not-toggle']}>
                      <SelectButton
                        value={group.isNot ? 'NOT' : ''}
                        options={[{ label: 'NOT', value: 'NOT' }]}
                        onChange={(e) => handleIsNotChange(e.value === 'NOT')}
                        unselectable
                      />
                    </div>
                    <LogicalOperatorToggle value={group.logicalOperator} onChange={handleLogicalOperatorChange} />
                  </div>
                )}
              </div>

              <div className={getExpressionCellClass(index, expressions.length)}>
                {item.expressionGroup ? (
                  <div className={styles['expression-group']}>
                    <RuleGroup
                      group={item.expressionGroup}
                      level={level + 1}
                      onUpdate={(updatedGroup) => handleUpdateNestedGroup(index, updatedGroup)}
                      fieldMetadata={fieldMetadata}
                      rightFieldMetadata={rightFieldMetadata}
                      operators={operators}
                      allowCustomFields={allowCustomFields}
                    />
                  </div>
                ) : (
                  <SimpleExpressionForm
                    initialValue={item.expression || undefined}
                    onCancel={() => handleDeleteExpression(index)}
                    onChange={(expr) => handleUpdateExpression(index, expr)}
                    showDelete
                    onAddGroup={() => handleCreateNestedGroup(index)}
                    showAddGroup={hasMultipleExpressions && !item.expressionGroup}
                    fieldMetadata={fieldMetadata}
                    rightFieldMetadata={rightFieldMetadata}
                    operators={operators}
                    allowCustomFields={allowCustomFields}
                  />
                )}
              </div>
            </Fragment>
          ))}
        </div>
      )}

      <Button
        type="button"
        label="Add Rule"
        icon="pi pi-plus"
        link
        size="small"
        onClick={handleAddRule}
        disabled={hasIncompleteExpression}
        className="font-bold mt-2"
        style={{ width: 'auto', color: hasIncompleteExpression ? '#9ca3af' : undefined }}
      />
    </div>
  );
};
